import { Claude, DeepSeek, Gemini, Grok, OpenAI, Perplexity } from "@lobehub/icons";
import { Button } from "@promptwatch/ui/components/button";
import { useIsMobile } from "@promptwatch/ui/hooks/use-mobile";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useState } from "react";
import BaseLayout from "@/components/base-layout";
import BrandCloud from "@/components/brand-cloud";
import { Header } from "@/components/header";
import { MockupDashboard } from "@/components/mockup-dashboard";
import FAQSection from "@/components/sections/faq";
import FeaturesSection from "@/components/sections/features-bento";
import FeaturesDashboardSection from "@/components/sections/features-dashboard";
import PricingSection from "@/components/sections/pricing";
import ProblemSection from "@/components/sections/problem";
import Solution from "@/components/sections/solution";
import TestimonialsSection from "@/components/testimonial";
import { clientEnv } from "@/env/client";

export default function Home() {
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const posthog = usePostHog();
  const router = useRouter();
  const isMobile = useIsMobile();

  const logos = [
    { component: OpenAI.Combine, type: undefined },
    { component: Claude.Combine, type: "color" },
    { component: DeepSeek.Combine, type: "color" },
    { component: Gemini.Combine, type: "color" },
    { component: Perplexity.Combine, type: "color" },
    { component: Grok.Combine, type: undefined },
    // { component: Meta.Combine, type: "color" },
    // { component: Qwen.Combine, type: "color" },
    // { component: Gemma.Combine, type: "color" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex((prevIndex) => (prevIndex + 1) % logos.length);
    }, 2000); // Change logo every 2 seconds

    return () => clearInterval(interval);
  }, [logos.length]);

  return (
    <BaseLayout>
      <Header />
      <main className="overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-primary-600" />
        <div className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]" />

        <section id="home" className="relative">
          <div className="relative mx-auto max-w-6xl border-x border-b border-gray-950/[0.07] px-3 pb-10 pt-6">
            <h1 className="text-title mx-auto mt-6 text-center text-4xl font-medium sm:mt-12 sm:text-5xl lg:text-5xl flex justify-center gap-2 items-center">
              Get your company mentioned by <span className="sr-only">ChatGPT, Claude, Gemini, Perplexity, and other AI platforms</span>
            </h1>
            <div className="flex justify-center gap-2 items-center mt-5 h-[70px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentLogoIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                >
                  {React.createElement(logos[currentLogoIndex].component, {
                    size: isMobile ? 50 : 58,
                    type: logos[currentLogoIndex].type as "color" | "mono" | undefined,
                  })}
                </motion.div>
              </AnimatePresence>
            </div>
            <h2 className="text-lg sm:text-xl  mx-auto my-8 max-w-3xl text-center text-gray-800">
              Boost visibility in ChatGPT, Claude, Perplexity, and other AI search engines, monitor mentions, and keep your business top of
              mind.
            </h2>
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                className="bg-white px-4"
                size="lg"
                onClick={() => {
                  posthog.capture("join_with_google_button_clicked", {
                    source: "hero",
                  });
                }}
                href={`${clientEnv.NEXT_PUBLIC_APP_URL}/redirect-google-login`}
              >
                <div className="flex items-center">
                  <svg className="mr-2 size-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Join with Google
                </div>
              </Button>
              <Button
                variant="default"
                className="flex items-center gap-2 px-5"
                size="lg"
                onClick={() => {
                  posthog.capture("get_started_button_clicked", {
                    source: "hero",
                  });

                  router.push(clientEnv.NEXT_PUBLIC_APP_URL);
                }}
              >
                Start 7-day trial <ArrowRight className="size-4" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-4 pt-6">
              <p className="text-md text-gray-500 font-medium">
                Talk to us first?{" "}
                <Link
                  href="/book-a-demo"
                  onClick={() => {
                    posthog.capture("book_demo_button_clicked", {
                      source: "hero",
                    });
                  }}
                  className="text-primary underline hover:text-primary-600"
                >
                  Book a demo
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section className="relative border-b">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -left-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -right-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07]">
              <MockupDashboard />
            </div>
          </div>
        </section>

        {/* <section className="border-b">
          <div className="mx-auto max-w-6xl border-x">
            <AILogoCloud />
          </div>
        </section> */}

        <section className="border-b">
          <div className="mx-auto max-w-6xl border-x px-3">
            <div className="p-10 px-0 z-10 relative">
              <div className="text-body text-center text-sm font-medium">Trusted by 500+ Companies & Agencies</div>
              <BrandCloud />
            </div>
          </div>
        </section>

        <ProblemSection />
        <div className="pb-20">
          <TestimonialsSection
            testimonial={
              <p className="font-medium text-muted-foreground">
                With the growing demand for <span className="font-semibold text-black">GEO visibility</span> for our clients, we have been
                searching for a tool that can provide actionable information. Promptwatch has enabled us to{" "}
                <span className="font-semibold text-black">understand what LLM platforms are looking for</span> and how are they using our
                clients' content.
              </p>
            }
            author="Jimmy Park"
            company="Head of Search at Opascope"
            avatar="/people/jimmy.jpeg"
          />
        </div>

        <section className="relative border" id="platform">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -left-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -right-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07]">
              <FeaturesDashboardSection />
            </div>
          </div>
        </section>

        <Solution />
        <div className="pb-20">
          <TestimonialsSection
            testimonial={
              <p className="font-medium text-muted-foreground">
                We use Promptwatch to monitor our clients' visibility across AI platforms like ChatGPT and Perplexity. It provides{" "}
                <span className="font-semibold text-black">data-driven recommendations</span> and helps us{" "}
                <span className="font-semibold text-black">demonstrate clear ROI</span> to clients - it's essential for our AI SEO offering.
              </p>
            }
            author="Mohs Klunder"
            company="Owner at Goochem Media"
            avatar="/people/mohs.png"
          />
        </div>

        <section className="relative border ">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -left-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -right-1 -top-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07]">
              <FeaturesSection />
            </div>
          </div>
        </section>

        <PricingSection />

        <section className="border-b">
          <div className="mx-auto max-w-6xl border-x py-5">
            <div className="p-10 px-0 z-10 relative px-3 ">
              <div className="text-body text-center text-sm font-medium">Trusted by 500+ Companies & Agencies</div>
              <BrandCloud />
            </div>

            <div className="pb-12 pt-20 border-t border-gray-950/[0.07]">
              <TestimonialsSection
                testimonial={
                  <p className="font-medium text-muted-foreground">
                    We help clients get discovered across Google, social media, and{" "}
                    <span className="font-semibold text-black">AI platforms like Google SGE and Perplexity</span>. With Promptwatch, we{" "}
                    <span className="font-semibold text-black">track visibility in AI-generated answers</span> and optimize based on that
                    data.
                  </p>
                }
                author="Rens de Jonge"
                company="Owner at Revisto Marketing"
                avatar="/people/rens.jpeg"
              />
            </div>
          </div>
        </section>
        <section className="relative border-b overflow-hidden border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <FAQSection />
            </div>
          </div>
        </section>
        {/* <FAQ /> */}
      </main>
    </BaseLayout>
  );
}
